/**
 * Client-side RBAC Permission Definitions
 * Mirror of src/config/permissions.js — keep in sync
 */

export const VALID_ROLES = ['super_admin', 'practice_admin', 'clinical_admin', 'care_provider'];

const ALL = [...VALID_ROLES];
const ADMINS = ['super_admin', 'practice_admin'];
const ADMINS_CLINICAL = ['super_admin', 'practice_admin', 'clinical_admin'];
const SUPER_ONLY = ['super_admin'];

export const PERMISSIONS = {
  'patients:view':                ALL,
  'patients:create':              ADMINS_CLINICAL,
  'patients:update':              ADMINS_CLINICAL,
  'patients:opt_out':             ADMINS_CLINICAL,

  'sms:view':                     ALL,
  'sms:send':                     ALL,
  'sms:consent':                  ADMINS_CLINICAL,
  'sms:bulk_consent':             ADMINS,
  'sms:ai_queue':                 ADMINS_CLINICAL,
  'sms:ai_config':                ADMINS,
  'sms:ai_analytics':             ADMINS_CLINICAL,
  'sms:sync':                     ADMINS_CLINICAL,

  'voice:call':                   ALL,
  'voice:history':                ALL,
  'voice:recordings':             ALL,
  'voice:callbacks':              ALL,
  'voice:dashboard':              ADMINS_CLINICAL,
  'voice:assistants':             ADMINS,
  'voice:config':                 ADMINS,
  'voice:tools':                  ADMINS,
  'voice:departments':            SUPER_ONLY,
  'voice:providers':              ADMINS,
  'voice:booking_configs':        ADMINS,
  'voice:schedules':              ALL,
  'voice:consent':                ALL,
  'voice:token':                  ALL,

  'campaigns:view':               ADMINS_CLINICAL,
  'campaigns:run':                ADMINS,
  'campaigns:configure':          ADMINS,
  'campaigns:types':              ADMINS,
  'campaigns:cadences':           ADMINS,
  'campaigns:cleanup':            SUPER_ONLY,

  'reports:dashboard':            ADMINS_CLINICAL,
  'reports:campaign':             ADMINS_CLINICAL,
  'reports:messaging':            ADMINS_CLINICAL,
  'reports:revenue':              ADMINS,
  'reports:patient_engagement':   ALL,
  'reports:activity':             ADMINS_CLINICAL,
  'reports:short_links':          ADMINS_CLINICAL,

  'notifications:view':           ADMINS_CLINICAL,
  // commons #231: mirrors src/config/permissions.js — clinical_admin may edit rules/templates
  'notifications:manage':         ADMINS_CLINICAL,
  'notifications:send':           ADMINS_CLINICAL,
  'notifications:history':        ADMINS_CLINICAL,

  'billing:view':                 ADMINS,
  'billing:configure':            ADMINS,
  'billing:escalations':          ADMINS,
  'billing:revenue_flow':         ADMINS,

  'reputation:view':              ADMINS_CLINICAL,
  'reputation:configure':         ADMINS,
  'reputation:consent':           ADMINS_CLINICAL,
  'reputation:request':           ADMINS_CLINICAL,

  'settings:view':                ADMINS_CLINICAL,
  'settings:update':              ADMINS,
  'settings:branding_view':       ALL,
  'settings:onboarding':          ADMINS,

  'storage:practice_view':        ADMINS_CLINICAL,
  'storage:practice_upload':      ADMINS_CLINICAL,
  'storage:practice_delete':      ADMINS,
  'storage:patient_files':        ALL,
  'storage:patient_delete':       ADMINS,
  'storage:recordings':           ALL,
  'storage:encounter_files':      ALL,
  'storage:download_url':         ALL,
  'storage:category_list':        ADMINS_CLINICAL,

  'ehr:sync':                     ADMINS_CLINICAL,
  'ehr:status':                   ADMINS_CLINICAL,
  'ehr:mappings':                 ADMINS,
  'ehr:appointment_types_view':   ALL,
  'ehr:appointment_types_update': ADMINS,
  'ehr:reason_codes_view':        ALL,
  'ehr:reason_codes_update':      ADMINS,
  'ehr:booking':                  ADMINS,

  'chat:sessions':                ADMINS_CLINICAL,
  'chat:takeover':                ADMINS_CLINICAL,
  'chat:config_view':             ADMINS_CLINICAL,
  'chat:config_update':           ADMINS,

  'intake:view':                  ALL,
  'intake:manage':                ADMINS,
  'intake:generations':           ADMINS_CLINICAL,

  'consent:stats':                ADMINS_CLINICAL,
  'consent:view':                 ALL,
  'consent:update':               ALL,

  'users:manage':                 SUPER_ONLY,

  // Phone Management (super_admin only)
  'phone_management:view':        SUPER_ONLY,
  'phone_management:manage':      SUPER_ONLY,

  'practice_groups:view':         ADMINS,
  'practice_groups:manage':       SUPER_ONLY,
  'practices:create':             SUPER_ONLY,
  'practices:enable_disable':     SUPER_ONLY,
  'assignments:manage':           SUPER_ONLY,

  'audit:hipaa':                  SUPER_ONLY,
  'audit:admin':                  SUPER_ONLY,

  'baa:manage':                   SUPER_ONLY,

  // Compliance Engine — Platform Layer
  'compliance:platform:view':     ADMINS,
  'compliance:platform:manage':   SUPER_ONLY,

  // Compliance Engine — Practice Layer
  'compliance:view':              ADMINS_CLINICAL,
  'compliance:risk':              ADMINS,
  'compliance:training':          ADMINS,
  'compliance:vendor':            ADMINS,
  'compliance:incident':          ADMINS_CLINICAL,
  'compliance:incident:anonymous': ALL,
  'compliance:export':            ADMINS,
  'compliance:attestation':       ALL,

  'appointments:view':            ALL,

  'templates:view':               ADMINS_CLINICAL,
  'templates:manage':             ADMINS,

  'scribe:view':                  ALL,
  'scribe:start':                 ALL,
  'scribe:notes_edit':            ALL,
  'scribe:notes_approve':         ADMINS_CLINICAL,
  'scribe:delete':                ADMINS,
  'scribe:stats':                 ADMINS_CLINICAL,

  'ai:insights':                  ADMINS_CLINICAL,
  'ai:config':                    ADMINS,

  'tags:view':                    ALL,
  'tags:manage':                  ADMINS,

  'flow:view':                    ALL,
  'flow:manage':                  ADMINS_CLINICAL,

  'adoption:view':                ALL,
  'adoption:manage':              ADMINS,
  'adoption:analytics':           ADMINS,

  // Waitlist Management
  'waitlist:view':                ADMINS_CLINICAL,
  'waitlist:manage':              ADMINS,

  // System / practice logs — kept in sync with backend
  'logs:system':                  SUPER_ONLY,
  'logs:practice':                ADMINS,
};

export function hasPermission(role, permission) {
  const allowed = PERMISSIONS[permission];
  if (!allowed) return false;
  return allowed.includes(role);
}

const permissionsConfig = { VALID_ROLES, PERMISSIONS, hasPermission };
export default permissionsConfig;
